// Pantalla Cartera (`/credit/portfolio`): el explorer de 3 columnas del diseño —filtros · tabla ·
// ficha—. Las filas salen de `api/portfolioSource`; el deep link `?credit=` abre un crédito directo.
import { useCallback, useMemo, useState } from 'react';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { TableToolbar } from '@/shared/patterns/TableToolbar';
import {
    CREDITS_NOUN,
    LoadedScopeFooter,
    closeSentence,
    loadedCountsNote,
    loadedFiltersNote,
    type LoadedScope,
} from '@/shared/patterns/LoadedScope';
import { useAuth } from '@/hooks/useAuth';
import { CreditFile } from '../creditFile';
import { hasPortfolioRows, usePortfolioSource } from './api/portfolioSource';
import {
    activeFilterCount,
    applyStatFilter,
    computeStats,
    filterPortfolio,
    listBrands,
    listStores,
    sortPortfolio,
} from './logic';
import { resolvePortfolioAbilities } from './permissions';
import {
    emptyPortfolioFilters,
    type PortfolioFilters,
    type PortfolioRow,
    type PortfolioSort,
    type PortfolioStatKey,
} from './types';
import { PortfolioFiltersPanel } from './components/PortfolioFiltersPanel';
import { PortfolioStatsBar } from './components/PortfolioStatsBar';
import { PortfolioTable, type PortfolioUnavailable } from './components/PortfolioTable';
import { usePortfolioPaginationTable } from './components/portfolioTableModel';

/** 🆕 nuestro: el mock abre por folio, pero con datos reales eso entierra la cartera viva. */
const DEFAULT_SORT: PortfolioSort = { key: 'origination', direction: 'desc' };

/** Sin filas leídas la tabla no pagina nada, pero el motor necesita un arreglo estable igual. */
const NO_ROWS: PortfolioRow[] = [];

function noLoadMore(): void {
    // Sin páginas pendientes el pie no pinta el botón; el motor igual pide una función.
}

const LOADING_NOTE: PortfolioUnavailable = {
    title: 'Cargando la cartera…',
    body: 'Estamos leyendo los créditos del tenant y el estado de sus equipos.',
};

/** El motivo del silo va tal cual: es lo único que distingue un 403 de una caída del servicio. */
function readFailureNote(message: string): PortfolioUnavailable {
    const reason = closeSentence(message);
    return {
        title: 'No se pudo cargar la cartera',
        body: `${reason} Esto NO significa que no haya créditos: la lectura falló.`,
    };
}

export interface PortfolioPageProps {
    /** Deep link `?credit=`: abre ese crédito en la ficha aunque la tabla no tenga filas. */
    credit?: string;
}

export function PortfolioPage({ credit }: PortfolioPageProps) {
    const { permissions } = useAuth();
    const abilities = useMemo(() => resolvePortfolioAbilities(permissions), [permissions]);
    const source = usePortfolioSource(abilities.canReadCredit);
    const [filters, setFilters] = useState<PortfolioFilters>(emptyPortfolioFilters);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [stat, setStat] = useState<PortfolioStatKey>('all');
    const [sort, setSort] = useState<PortfolioSort>(DEFAULT_SORT);
    const [pickedCreditId, setPickedCreditId] = useState<string | null>(null);

    const ready = hasPortfolioRows(source) ? source : null;
    const allRows = ready === null ? null : ready.rows;
    const unavailable = source.status === 'error' ? readFailureNote(source.message) : LOADING_NOTE;
    // No nulo = quedan páginas por traer, así que lo cargado NO es la cartera y la pantalla lo dice.
    const pending = ready !== null && ready.hasNextPage ? ready : null;
    // Sin filas leídas no hay universo del que hablar; con ellas, `hasMore` decide si el rótulo se pinta.
    const scope: LoadedScope | null =
        allRows === null ? null : { loaded: allRows.length, hasMore: pending !== null };

    const visibleRows = useMemo(() => {
        if (allRows === null) return null;
        return sortPortfolio(filterPortfolio(applyStatFilter(allRows, stat), filters), sort);
    }, [allRows, stat, filters, sort]);

    const stats = useMemo(() => computeStats(allRows), [allRows]);
    const brands = useMemo(() => listBrands(allRows ?? []), [allRows]);
    const stores = useMemo(() => listStores(allRows ?? []), [allRows]);

    // Corta lo YA cargado en páginas; los controles del pie leen de aquí.
    const table = usePortfolioPaginationTable(visibleRows ?? NO_ROWS);
    const modelRows = table.getRowModel().rows;
    const pageRows = useMemo(
        () => (visibleRows === null ? null : modelRows.map((row) => row.original)),
        [visibleRows, modelRows],
    );

    // El deep link manda hasta que el operador elija una fila; sin tabla es la única vía de entrada.
    const selectedCreditId = pickedCreditId ?? credit?.trim() ?? null;
    const countsNote = scope === null ? null : loadedCountsNote(scope, CREDITS_NOUN);
    const filtersNote = scope === null ? null : loadedFiltersNote(scope, CREDITS_NOUN);

    // Cambiar el filtro cambia el universo: dejar la página en la 4 mostraría un hueco vacío.
    const handleFiltersChange = useCallback(
        (next: PortfolioFilters) => {
            setFilters(next);
            table.setPageIndex(0);
        },
        [table],
    );

    const handleStatChange = useCallback(
        (next: PortfolioStatKey) => {
            setStat(next);
            table.setPageIndex(0);
        },
        [table],
    );

    if (!abilities.canReadCredit) {
        return (
            <div role="alert" className="p-6 text-[12.5px] text-muted-foreground">
                No tienes permiso para ver la cartera de créditos. Si crees que es un error,
                contacta al administrador.
            </div>
        );
    }

    return (
        <ScreenFrame>
            <MasterDetailExplorer
                filtersCollapsed={filtersCollapsed}
                filters={
                    <PortfolioFiltersPanel
                        filters={filters}
                        onChange={handleFiltersChange}
                        brands={brands}
                        stores={stores}
                        loadedNote={filtersNote}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((prev) => !prev);
                        }}
                    />
                }
                list={
                    <>
                        {countsNote === null ? null : (
                            <div
                                role="status"
                                className="flex-none border-b border-warning/40 bg-warning/10 px-2 py-1.5 text-[11px] leading-snug text-foreground"
                            >
                                {countsNote}
                            </div>
                        )}
                        <PortfolioStatsBar
                            stats={stats}
                            active={stat}
                            onChange={handleStatChange}
                            disabled={allRows === null}
                            scope={scope}
                        />
                        {/* Sin filtros puestos la barra no se pinta: sería una banda vacía que
                            solo le roba alto a la tabla. */}
                        {activeFilterCount(filters) > 0 ? (
                            <TableToolbar>
                                <span className="rounded bg-primary/10 px-1.5 py-[1px] text-[10px] font-bold text-primary">
                                    {activeFilterCount(filters)} filtros
                                </span>
                            </TableToolbar>
                        ) : null}
                        <PortfolioTable
                            rows={pageRows}
                            unavailable={unavailable}
                            sort={sort}
                            onSortChange={setSort}
                            selectedCreditId={selectedCreditId}
                            onSelect={setPickedCreditId}
                        />
                        <LoadedScopeFooter
                            noun={CREDITS_NOUN}
                            scope={scope}
                            shown={visibleRows?.length ?? null}
                            readFailed={source.status === 'error'}
                            table={table}
                            hasNextPage={pending !== null}
                            isFetchingNextPage={ready?.isFetchingNextPage ?? false}
                            onLoadMore={ready?.fetchNextPage ?? noLoadMore}
                            nextPageError={ready?.nextPageError ?? null}
                        />
                    </>
                }
                detail={<CreditFile creditId={selectedCreditId} />}
            />
        </ScreenFrame>
    );
}
